import { SubscriptionFeature, subscriptionFeaturesByTier } from '@upright/common/subscription/subscription-features';
import { useIsTeam } from '@upright/ui/common/generic/hooks';
import { useGetActiveSubscription } from './use-get-active-subscription';
import { useGetCompanySubscription } from './use-get-company-subscription';

export function useGetHasAccessToFeatureWithCurrentSubscription(
  feature: SubscriptionFeature,
  companyId?: string,
): {
  hasAccess: boolean;
  isPending: boolean;
} {
  const isTeamUser = useIsTeam();
  const orgSubscription = useGetActiveSubscription();

  // Resolve the subscription server-side whenever a company is in context. The
  // GET /subscription/company/{companyId} endpoint is authoritative for every org type: for a
  // DMA Consultant org it returns that company's own tier (derived from its payment status), and
  // for all other orgs it delegates to the org-level plan. We deliberately do NOT gate this on a
  // browser-derived org-type check (e.g. isDmaConsultantUser): that check depends on group lineage
  // being fully populated in /own-info, and when it isn't, the company query was silently skipped
  // and the UI fell back to the org-level Starter tier — locking attributes for paid Enterprise
  // consultant companies (PLT-5753).
  const companySubscription = useGetCompanySubscription(companyId ?? '', !!companyId);
  const { data: subscription, isPending: isPendingSubscription } = companyId ? companySubscription : orgSubscription;

  // Team users always have access regardless of the company's subscription tier
  if (isTeamUser) {
    return { hasAccess: true, isPending: false };
  }

  return {
    hasAccess: subscriptionFeaturesByTier[subscription?.subscription.tier || 'starter'].includes(feature),
    isPending: isPendingSubscription,
  };
}
